import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { useSupabase } from '@/hooks/useSupabase';
import { capitalizeBiometricLabel, getBiometricInlinePhrase } from '@/lib/biometric';

interface AppAccessFormProps {
  /** When true, the password-confirm modal is opened automatically on mount. */
  autoOpenEnable?: boolean;
  /** Called once the auto-open has been consumed so the parent can clear its flag. */
  onConsumedAutoOpen?: () => void;
}

export default function AppAccessForm({ autoOpenEnable, onConsumedAutoOpen }: AppAccessFormProps = {}) {
  const {
    user,
    biometricAvailable,
    biometricEnabled,
    biometricLabel,
    enableBiometric,
    disableBiometric,
    signInWithPassword,
  } = useSupabase();

  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);

  // Honour an auto-open request from the parent (e.g. arriving from the
  // biometric setup banner). Skip if biometric isn't available or is already
  // enabled, since the modal would be pointless in those cases.
  useEffect(() => {
    if (!autoOpenEnable) return;
    if (Platform.OS === 'web') return;
    if (!biometricAvailable || biometricEnabled) {
      onConsumedAutoOpen?.();
      return;
    }
    setPassword('');
    setConfirmError(null);
    setConfirmModalVisible(true);
    onConsumedAutoOpen?.();
  }, [autoOpenEnable, biometricAvailable, biometricEnabled, onConsumedAutoOpen]);

  if (Platform.OS === 'web') {
    return null;
  }

  const inlinePhrase = getBiometricInlinePhrase(biometricLabel);
  const sentenceStart = capitalizeBiometricLabel(biometricLabel);

  const handleToggle = async (next: boolean) => {
    if (toggleBusy || confirmSubmitting) return;

    if (next) {
      setPassword('');
      setConfirmError(null);
      setConfirmModalVisible(true);
      return;
    }

    setToggleBusy(true);
    try {
      await disableBiometric();
      Toast.show({
        type: 'success',
        text1: 'Quick sign-in disabled',
      });
    } catch (err: any) {
      Toast.show({
        type: 'error',
        text1: 'Could not disable',
        text2: err?.message ?? 'Please try again.',
      });
    } finally {
      setToggleBusy(false);
    }
  };

  const handleConfirmEnable = async () => {
    if (!user?.email) {
      setConfirmError('No signed-in user available.');
      return;
    }
    if (password.length < 8) {
      setConfirmError('Please enter your password.');
      return;
    }

    setConfirmError(null);
    setConfirmSubmitting(true);
    try {
      // Revalidate the password against Supabase before persisting it.
      await signInWithPassword(user.email, password);
      await enableBiometric(user.email, password);
      setConfirmModalVisible(false);
      setPassword('');
      Toast.show({
        type: 'success',
        text1: 'Quick sign-in enabled',
      });
    } catch (err: any) {
      setConfirmError(err?.message ?? 'Could not verify your password.');
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    if (confirmSubmitting) return;
    setConfirmModalVisible(false);
    setPassword('');
    setConfirmError(null);
  };

  return (
    <>
      <Card style={styles.card}>
        <View style={styles.headerContainer}>
          <View style={styles.iconWrapper}>
            <Ionicons name="lock-closed" size={18} color="white" />
          </View>
          <Text style={styles.headerTitle}>App access</Text>
        </View>

        <View style={styles.cardContent}>
          <View style={styles.row}>
            <View style={styles.rowText}>
              <Text style={styles.rowTitle}>Quick sign-in</Text>
              <Text style={styles.rowSubtitle}>
                {biometricAvailable
                  ? `Skip typing your password by signing in with ${inlinePhrase}.`
                  : `${sentenceStart} is not available on this device. Set up a passcode and enrol biometrics in your device settings to enable this feature.`}
              </Text>
            </View>
            <Switch
              checked={biometricEnabled}
              onCheckedChange={handleToggle}
              disabled={!biometricAvailable || toggleBusy}
            />
          </View>

          <View style={styles.infoBox}>
            <Ionicons name="information-circle-outline" size={16} color="#666" />
            <Text style={styles.infoText}>
              Your credentials are stored on this device only, protected by {inlinePhrase} or your device passcode. They are never synced or backed up.
            </Text>
          </View>
        </View>
      </Card>

      <Modal
        visible={confirmModalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCancelConfirm}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Confirm your password</Text>
            <Text style={styles.modalSubtitle}>
              Re-enter the password for {user?.email ?? 'your account'} to set up quick sign-in with {inlinePhrase}.
            </Text>

            <Text style={styles.fieldLabel}>Password</Text>
            <Input
              placeholder="Enter your password"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="current-password"
              textContentType="password"
              secureTextEntry
              value={password}
              onChangeText={(text: string) => {
                setPassword(text);
                if (confirmError) setConfirmError(null);
              }}
              className="border-gray-300 bg-white"
            />

            {confirmError ? <Text style={styles.errorText}>{confirmError}</Text> : null}

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalCancel]}
                onPress={handleCancelConfirm}
                disabled={confirmSubmitting}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <Button
                size="default"
                variant="crew"
                onPress={handleConfirmEnable}
                style={styles.modalConfirm}
                disabled={confirmSubmitting}
              >
                {confirmSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  'Enable'
                )}
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    marginBottom: 16,
    overflow: 'hidden',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#222',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  headerTitle: {
    color: 'white',
    fontWeight: '600',
    fontSize: 16,
  },
  cardContent: {
    padding: 16,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#222',
    marginBottom: 4,
  },
  rowSubtitle: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f5f5f7',
  },
  infoText: {
    flex: 1,
    fontSize: 12,
    color: '#666',
    lineHeight: 17,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000',
    marginBottom: 6,
  },
  modalSubtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
    lineHeight: 19,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#222',
    marginBottom: 6,
  },
  errorText: {
    color: '#d32f2f',
    fontSize: 13,
    marginTop: 8,
  },
  modalActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 12,
    marginTop: 20,
  },
  modalButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  modalCancel: {
    backgroundColor: 'transparent',
  },
  modalCancelText: {
    color: '#666',
    fontSize: 15,
    fontWeight: '600',
  },
  modalConfirm: {
    minWidth: 120,
    height: 44,
    borderRadius: 8,
  },
});
